import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { CreditCard, Check, ExternalLink, Wallet, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Stagger, StaggerItem, Reveal } from "../components/Reveal";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { useBooking } from "../context/BookingContext";
import { FinancingCalculator } from "../components/FinancingCalculator";
import api from "../lib/api";

export default function Financing() {
  const [partners, setPartners] = useState([]);
  const [payment, setPayment] = useState(null);
  const { openBooking } = useBooking();

  useEffect(() => {
    api.get("/financing-partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/payment-info").then((r) => setPayment(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="financing-page">
      <Seo title="Financing & Payment Options" description="Flexible financing for aesthetic treatments in Clinton Township, MI — Cherry, CareCredit, Affirm and Klarna. 0% APR plans available for qualified applicants." path="/financing" />
      <PageHeader
        overline="Financing & Payment"
        title="Beautiful skin, flexible payments"
        subtitle="We partner with trusted providers so you can begin your treatments today and pay over time — often with 0% interest options."
      />

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Stagger className="grid sm:grid-cols-2 gap-6">
            {partners.map((p) => (
              <StaggerItem key={p.name}>
                <div className="bg-white rounded-2xl p-8 border border-slate-100 hover-lift h-full flex flex-col" data-testid={`partner-${p.name.toLowerCase()}`}>
                  <div className="flex items-center justify-between">
                    <CreditCard className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
                    {p.highlight && (
                      <span className="text-xs iri-gradient text-ink font-medium px-3 py-1 rounded-full">{p.highlight}</span>
                    )}
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-ink mt-5">{p.name}</h3>
                  <p className="text-sm text-[#5C6664] mt-2 leading-relaxed">{p.blurb}</p>

                  <div className="grid grid-cols-3 gap-3 mt-5 py-4 border-y border-slate-100">
                    <div>
                      <p className="text-xs text-[#8A9492]">APR</p>
                      <p className="text-sm text-ink font-medium mt-0.5">{p.apr}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#8A9492]">Terms</p>
                      <p className="text-sm text-ink font-medium mt-0.5">{p.terms}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#8A9492]">Min.</p>
                      <p className="text-sm text-ink font-medium mt-0.5">{p.min_amount}</p>
                    </div>
                  </div>

                  {p.how_to_qualify?.length > 0 && (
                    <ul className="mt-4 space-y-2 flex-1">
                      {p.how_to_qualify.map((q) => (
                        <li key={q} className="flex items-start gap-2.5 text-sm text-[#5C6664]">
                          <Check className="h-4 w-4 mt-0.5 text-iri-mid shrink-0" /> {q}
                        </li>
                      ))}
                    </ul>
                  )}

                  <a href={p.apply_url} target="_blank" rel="noopener noreferrer" className="mt-6" data-testid={`apply-${p.name.toLowerCase()}`}>
                    <Button variant="outline" className="rounded-full w-full border-ink/20 text-ink hover:bg-[rgba(224,195,252,0.12)]">
                      Apply with {p.name} <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <FinancingCalculator />
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="grid lg:grid-cols-2 gap-10 items-center bg-white rounded-[2rem] p-8 md:p-14 border border-slate-100">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-light text-ink">How it works</h2>
              <ul className="mt-6 space-y-4">
                {[
                  "Choose your treatment and financing partner during your consultation",
                  "Apply in minutes with a soft credit check — no impact to your score",
                  "Get an instant decision and select a plan that fits your budget",
                  "Start your treatments right away and pay monthly",
                ].map((step) => (
                  <li key={step} className="flex items-start gap-3 text-[#5C6664]">
                    <span className="h-6 w-6 rounded-full iri-gradient flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5 text-ink" />
                    </span>
                    {step}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => openBooking()}
                  data-testid="financing-book-button"
                  className="iri-gradient text-ink rounded-full h-12 px-8 font-medium hover:scale-105 transition-transform"
                >
                  Book a consultation
                </Button>
                {payment?.one_time_payment_url && (
                  <a href={payment.one_time_payment_url} target="_blank" rel="noopener noreferrer" data-testid="one-time-payment-link">
                    <Button variant="outline" className="rounded-full h-12 px-8 border-ink/20 text-ink hover:bg-[rgba(224,195,252,0.12)]">
                      Make a one-time payment <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
            <div className="iri-gradient rounded-3xl p-10 text-center">
              <p className="font-heading text-6xl font-light text-ink">0%</p>
              <p className="text-ink/70 mt-2">interest financing available on qualifying plans</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PAYMENT & PRICING */}
      {payment && (
        <section className="pb-24" data-testid="payment-pricing-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.2em] text-[#5C6664]">Payment &amp; Pricing</span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-light text-ink">
                Simple, transparent policies
              </h2>
            </Reveal>

            <div className="grid lg:grid-cols-3 gap-6 mt-8">
              <Reveal className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="accepted-payments">
                <Wallet className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-medium text-ink mt-4">Accepted payments</h3>
                <div className="flex flex-wrap gap-2 mt-4">
                  {payment.accepted_payments?.map((m) => (
                    <span key={m} className="text-xs bg-stone text-ink px-3 py-1.5 rounded-full border border-slate-100">{m}</span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.05} className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="deposit-policy">
                <ShieldCheck className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-medium text-ink mt-4">Deposit policy</h3>
                <p className="text-sm text-[#5C6664] mt-3 leading-relaxed">{payment.deposit_policy}</p>
              </Reveal>
              <Reveal delay={0.1} className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="cancellation-policy">
                <ShieldCheck className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-medium text-ink mt-4">Cancellation policy</h3>
                <p className="text-sm text-[#5C6664] mt-3 leading-relaxed">{payment.cancellation_policy}</p>
              </Reveal>
            </div>

            {payment.faqs?.length > 0 && (
              <div className="max-w-3xl mt-12">
                <h3 className="font-heading text-xl md:text-2xl font-light text-ink mb-4">Payment FAQs</h3>
                <Accordion type="single" collapsible className="w-full" data-testid="payment-faqs">
                  {payment.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`pf-${i}`} className="border-b border-slate-200">
                      <AccordionTrigger className="text-left font-heading text-ink font-medium hover:no-underline" data-testid={`payment-faq-trigger-${i}`}>
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#5C6664] leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
